import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Table, Empty, Tag } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faFilter } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetUsersQuery } from "../../../redux/users/userAPI";
import { updateOpenDrawerUser, updateUser } from "../../../redux/users/userSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// FILTERS
import FilterSearchText from "../../components/filters/FilterSearchText";
import FilterSelectUserStatus from "../../components/filters/FilterSelectUserStatus";
// UTILS
import { getTableRowClass, buildFilterString } from "../../../utils/utils";
import validatePermission from "../../../utils/validatePermissions";
// COMPONENTS
import TableColumnStatusUser from "../../components/table/TableColumnStatusUser";
import TableNoDataBox from "../../components/table/TableNoDataBox";
import UsersTableActions from "./UsersTableActions";
import UsersConfirmResetOfflinePswModal from "./UsersConfirmResetOfflinePswModal";
import UsersConfirmGetOfflinePswModal from "./UsersConfirmGetOfflinePswModal";
// INTERFACES
import { RootState } from "../../../app/store";

const UsersTable = () => {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const { theme } = useSelector((state: RootState) => state.home);
	const [t] = useTranslation("global");
	const { Column } = Table;

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [users, setUsers] = useState<any[]>([]);
	const [filterName, setFilterName] = useState("");
	const [filterEmail, setFilterEmail] = useState("");
	const [filterUserStatus, setFilterUserStatus] = useState("");
	const [errorMessage, setErrorMessage] = useState<any>("");
	const [pagination, setPagination] = useState<any>({
		current: 1,
		pageSize: 10,
		showSizeChanger: true,
		pageSizeOptions: ["10", "20", "50", "100"],
		total: 0,
	});
	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { userRefreshTable, permissions } = useSelector((state: RootState) => state.user);
	const { globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [triggerGetUsersTable, { data, isLoading, isError, isFetching, error }] = useLazyGetUsersQuery();

	// ************************************************ */
	// FUNCTIONS ************************************** */

	const onCellClick = (record: any) => {
		if (record) {
			dispatch(updateUser(record));
			dispatch(updateOpenDrawerUser(true));
		}
	};

	const getData = async (page: number, limit: number) => {
		let idOrganization = globalOrganizationId;
		if (userLogged && userLogged.org_id && userLogged.org_id === idOrganization) {
			idOrganization = "";
		}
		const searchAndFilterString = buildFilterString({
			filterFirstName: filterName,
			filterMiddleName: filterName,
			filterLastName: filterName,
			filterEmail,
			filterOrganizationFullName: idOrganization,
			filterUserStatus,
		});
		const token = await GETJwtToken();
		triggerGetUsersTable({
			page,
			limit,
			token,
			searchAndFilterString,
		});
	};

	const handleTableChange = async (pagination_: any) => {
		setPagination({
			...pagination,
			current: pagination_.current,
			pageSize: pagination_.pageSize,
			total: pagination_.total,
		});
		getData(pagination_.current, pagination_.pageSize);
	};

	const refreshTable = async () => {
		setPagination({
			...pagination,
			current: 1,
			pageSize: 10,
		});
		getData(1, 10);
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		getData(1, 10);
	}, []);

	useEffect(() => {
		if (data && data.data) {
			setPagination({
				...pagination,
				current: pagination.current,
				pageSize: pagination.pageSize,
				total: data.total,
			});
			setUsers(data.data);
		}
	}, [data]);

	useEffect(() => {
		if (isError) {
			setUsers([]);
			setPagination({
				...pagination,
				current: 1,
				pageSize: 10,
				total: 0,
			});
		}
	}, [isError]);

	useEffect(() => {
		if (userRefreshTable) {
			setFilterName("");
			setFilterEmail("");
			setFilterUserStatus("");
			refreshTable();
		}
	}, [userRefreshTable]);


	useEffect(() => {
		if (error) {
			setErrorMessage(error);
			setUsers([]);
			setPagination({
				current: 1,
				pageSize: 10,
				showSizeChanger: true,
				pageSizeOptions: ["10", "20", "50", "100"],
				total: 0,
			});
		}
	}, [error]);

	useEffect(() => {
		if (globalOrganizationId !== "") {
			refreshTable();
		}
	}, [globalOrganizationId]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='mt-3'>
			<>
				{validatePermission("users-view", permissions) ? (
					<Table
						locale={{
							emptyText: (
								<TableNoDataBox
									isError={isError}
									errorMessage={
										errorMessage && errorMessage.data && errorMessage.data.message ? errorMessage.data.message : "Error"
									}
									noItemsFound='No users found'
								/>
							),
						}}
						rowClassName={(record, index) => getTableRowClass(index, theme)}
						className='customTable'
						dataSource={users}
						loading={isLoading || isFetching}
						rowKey={(record) => record.id}
						size='small'
						pagination={pagination}
						onChange={handleTableChange}
						scroll={{ x: 900 }}
						footer={() => (
							<div className='generalStyles__flexEnd'>
								<Tag>{pagination.total} Users</Tag>
							</div>
						)}
					>
						{/** NAME */}
						<Column
							onCell={(record) => ({
								onClick: () => {
									onCellClick(record);
								},
							})}
							title={t("userManagement.name")}
							dataIndex='first_name'
							key='first_name'
							width='200px'
							filterIcon={<FontAwesomeIcon icon={faSearch} />}
							filterDropdown={() => (
								<FilterSearchText
									placeHolder={t("general.name")}
									filterValue={filterName}
									setFilterValue={setFilterName}
									refreshTable={refreshTable}
									refreshParameters={[]}
								/>
							)}
							render={(text, record: any) => (
								<div className='generalStyles__ellipsis__200px'>
									{record.first_name || ""} {record.middle_name || ""} {record.last_name || ""}
								</div>
							)}
						/>
						{/** EMAIL */}
						<Column
							onCell={(record) => ({
								onClick: () => {
									onCellClick(record);
								},
							})}
							title={t("userManagement.email")}
							dataIndex='email'
							key='email'
							filterIcon={<FontAwesomeIcon icon={faSearch} />}
							filterDropdown={() => (
								<FilterSearchText
									placeHolder={t("userManagement.email")}
									filterValue={filterEmail}
									setFilterValue={setFilterEmail}
									refreshTable={refreshTable}
									refreshParameters={[]}
								/>
							)}
							render={(text) => <div>{text || "--"}</div>}
						/>
						{/** ORGANIZATION */}
						<Column
							onCell={(record) => ({
								onClick: () => {
									onCellClick(record);
								},
							})}
							title={t("organizationManagement.organization")}
							dataIndex='org'
							key='org'
							render={(text) => <div>{text.name || "--"}</div>}
						/>
						{/** ROLE */}
						<Column
							onCell={(record) => ({
								onClick: () => {
									onCellClick(record);
								},
							})}
							title={t("userManagement.role")}
							dataIndex='role'
							key='role'
							render={(text) => <div>{text.name || "--"}</div>}
						/>
						{/** STATUS */}
						<Column
							onCell={(record) => ({
								onClick: () => {
									onCellClick(record);
								},
							})}
							title={t("userManagement.status")}
							dataIndex='status'
							key='status'
							filterIcon={<FontAwesomeIcon icon={faFilter} />}
							filterDropdown={() => (
								<FilterSelectUserStatus
									placeHolder={t("general.status")}
									setFilterValue={setFilterUserStatus}
									filterValue={filterUserStatus}
									refreshTable={refreshTable}
									refreshParameters={[]}
								/>
							)}
							render={(text) => <TableColumnStatusUser text={text} />}
						/>
						{/** ACTIONS */}
						<Column
							title={t("userManagement.actions")}
							key='action'
							width='100px'
							render={(_: any, record: any) => <UsersTableActions selectedItem={record} />}
						/>
					</Table>
				) : (
					<>
						<div className='generalStyles__noAccessToListTable'>
							<Empty description={t("userManagement.listNotAvailable")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
						</div>
					</>
				)}
			</>
			{/** ---------------------------------------------------- */}
			{/** CONFIRM GET OFFLINE PASSWORD */}
			<UsersConfirmGetOfflinePswModal />
			{/** ---------------------------------------------------- */}
			{/** CONFIRM RESET OFFLINE PASSWORD */}
			<UsersConfirmResetOfflinePswModal />
		</div>
	);
};

export default UsersTable;
